import { ECONOMY } from '@/config/economy';
import { PLAYER } from '@/config/player';
import { MAX_GUN_TIER, type WeaponCategory } from '@/config/weapons';
import { CARD_VALUES } from '@/config/cards';
import type { I18nKey } from '@/i18n';
import type { PlayerStats } from '@/upgrades/stats';

/** Snapshot of everything the panel rows read. */
export interface StatView {
  stats: PlayerStats;
  gunTier: number;
  hp: number;
  /**
   * Categories of the equipped weapons — category rows outside it stay
   * hidden. Absent (game-over removal preview): every category may show.
   */
  categories?: ReadonlyArray<WeaponCategory>;
}

export type StatGroup = 'offense' | 'defense' | 'mobility' | 'economy';
export const STAT_GROUPS: ReadonlyArray<StatGroup> = ['offense', 'defense', 'mobility', 'economy'];

export interface RowDef {
  key: I18nKey;
  /** Stat glyph shared with the cards (docs/12 C2 — less text dependency) */
  icon: string;
  group: StatGroup;
  /** True when a LOWER displayed value is the improvement (cooldown, price) */
  down?: boolean;
  /** Weapon category row — listed only while that category is equipped */
  category?: WeaponCategory;
  /** Listed even at the stock value (core vital) */
  always?: boolean;
  /** Stock-ship value: rows sitting at it are hidden (docs/03 W4) */
  base: number;
  value(v: StatView): number;
  fmt(n: number): string;
}

const pct = (n: number) => `${n >= 0 ? '+' : ''}${Math.round(n * 100)}%`;
const flat = (n: number) => `${n >= 0 ? '+' : ''}${n}`;
const plainPct = (n: number) => `${Math.round(n * 100)}%`;

const CATEGORIES: ReadonlyArray<WeaponCategory> = ['bullet', 'plasma', 'laser', 'bomb'];

function categoryRows(): RowDef[] {
  return CATEGORIES.flatMap((cat): RowDef[] => [
    {
      key: `statp.${cat}` as I18nKey, icon: `dmg-${cat}`, group: 'offense', category: cat,
      base: 0, value: (v) => v.stats.catDamage[cat] - 1, fmt: pct,
    },
    {
      key: `statp.${cat}rate` as I18nKey, icon: `rate-${cat}`, group: 'offense', category: cat,
      base: 0, value: (v) => v.stats.catFireRate[cat] - 1, fmt: pct,
    },
  ]);
}

const REROLL_STOCK = ECONOMY.rerollDiscountByStack[0]! - 1;

/** Every ownable stat, grouped; the panel lists the modified ones. */
export const ROWS: ReadonlyArray<RowDef> = [
  // ---- Offense
  { key: 'statp.damage', icon: 'damage', group: 'offense', base: 0, value: (v) => v.stats.damageMul - 1, fmt: pct },
  { key: 'statp.firerate', icon: 'firerate', group: 'offense', base: 0, value: (v) => v.stats.fireRateMul - 1, fmt: pct },
  ...categoryRows(),
  {
    key: 'statp.critchance', icon: 'critchance', group: 'offense', base: PLAYER.crit.baseChance,
    value: (v) => PLAYER.crit.baseChance + v.stats.critChanceBonus, fmt: plainPct,
  },
  {
    key: 'statp.critdamage', icon: 'critdamage', group: 'offense', base: PLAYER.crit.baseDamageMul,
    value: (v) => PLAYER.crit.baseDamageMul + v.stats.critDamageBonus,
    fmt: (n) => `×${n.toFixed(1)}`,
  },
  { key: 'statp.projspeed', icon: 'projspeed', group: 'offense', base: 0, value: (v) => v.stats.projSpeedMul - 1, fmt: pct },
  { key: 'statp.range', icon: 'range', group: 'offense', base: 0, value: (v) => v.stats.rangeMul - 1, fmt: pct },
  { key: 'statp.supershot', icon: 'supershot', group: 'offense', base: 0, value: (v) => v.stats.supershotMul - 1, fmt: pct },
  { key: 'statp.fan', icon: 'superfan', group: 'offense', base: 0, value: (v) => v.stats.superFanBonus, fmt: flat },
  {
    key: 'statp.energy', icon: 'energy', group: 'offense', base: PLAYER.energyPerHit,
    value: (v) => PLAYER.energyPerHit + v.stats.energyBonus, fmt: (n) => `${n}`,
  },
  {
    key: 'statp.tier', icon: 'barrel', group: 'offense', base: 1,
    value: (v) => v.gunTier, fmt: (n) => `${n}/${MAX_GUN_TIER}`,
  },
  // ---- Defense
  {
    key: 'statp.maxhp', icon: 'heart', group: 'defense', always: true, base: PLAYER.maxHp,
    value: (v) => PLAYER.maxHp + v.stats.bonusHp * PLAYER.cellHp, fmt: (n) => `${n}`,
  },
  { key: 'statp.shieldstart', icon: 'shieldstart', group: 'defense', base: 0, value: (v) => v.stats.shieldStart, fmt: flat },
  {
    key: 'statp.reduction', icon: 'bulwark', group: 'defense', base: 0,
    value: (v) => 1 - v.stats.damageTakenMul, fmt: plainPct,
  },
  {
    // Aegis Link reduction grows as damage cards stack — live value here
    key: 'statp.aegis', icon: 'aegis', group: 'defense', base: 0,
    value: (v) => v.stats.aegisLink
      ? Math.min(CARD_VALUES.aegis.cap, (v.stats.damageMul - 1) * CARD_VALUES.aegis.ratio)
      : 0,
    fmt: plainPct,
  },
  { key: 'statp.armorkill', icon: 'salvager', group: 'defense', base: 0, value: (v) => v.stats.armorOnKill, fmt: flat },
  { key: 'statp.iframes', icon: 'iframes', group: 'defense', base: 0, value: (v) => v.stats.iframeMul - 1, fmt: pct },
  { key: 'statp.regen', icon: 'regen', group: 'defense', base: 0, value: (v) => v.stats.regenCells * PLAYER.cellHp, fmt: flat },
  { key: 'statp.lifesteal', icon: 'lifesteal', group: 'defense', base: 0, value: (v) => v.stats.lifestealPct, fmt: pct },
  // ---- Mobility
  { key: 'statp.speed', icon: 'speed', group: 'mobility', base: 0, value: (v) => v.stats.speedMul - 1, fmt: pct },
  {
    key: 'statp.dashcd', icon: 'dashcd', group: 'mobility', down: true, base: 0,
    value: (v) => v.stats.dashCooldownMul - 1, fmt: pct,
  },
  // ---- Economy
  { key: 'statp.magnet', icon: 'magnet', group: 'economy', base: 0, value: (v) => v.stats.magnetMul - 1, fmt: pct },
  { key: 'statp.greed', icon: 'greed', group: 'economy', base: 0, value: (v) => v.stats.greedMul - 1, fmt: pct },
  {
    key: 'statp.discount', icon: 'discount', group: 'economy', down: true, base: 0,
    value: (v) => v.stats.discountMul - 1, fmt: pct,
  },
  {
    key: 'statp.rerolldisc', icon: 'rerolldiscount', group: 'economy', down: true, base: REROLL_STOCK,
    value: (v) => ECONOMY.rerollDiscountByStack[
      Math.min(v.stats.rerollDiscountSteps, ECONOMY.rerollDiscountByStack.length - 1)
    ]! - 1,
    fmt: pct,
  },
  {
    key: 'statp.cargo', icon: 'cargo', group: 'economy', base: ECONOMY.inventory.start,
    value: (v) => Math.min(ECONOMY.inventory.total, ECONOMY.inventory.start + v.stats.cargoSlots),
    fmt: (n) => `${n}/${ECONOMY.inventory.total}`,
  },
];

const EPS = 1e-9;

/** Row state for one panel build: current value + the preview delta, if any. */
export interface RowState {
  row: RowDef;
  cur: number;
  /** Value after the previewed change (null: no preview / unchanged) */
  next: number | null;
  /** The previewed change improves this stat */
  better: boolean;
}

/**
 * Rows to list (docs/03 W4): modified stats only, category rows only for
 * equipped categories — but a row the preview touches is always shown so
 * the «old ▸ new» delta is never hidden.
 */
export function visibleRows(current: StatView, next: StatView | null): RowState[] {
  const cats = current.categories;
  const out: RowState[] = [];
  for (const row of ROWS) {
    const cur = row.value(current);
    const after = next ? row.value(next) : cur;
    const changed = Math.abs(after - cur) > EPS;
    const modified = Math.abs(cur - row.base) > EPS;
    const catOk = !row.category || !cats || cats.includes(row.category);
    if (!changed && !row.always && !(modified && catOk)) continue;
    out.push({
      row, cur, next: changed ? after : null,
      better: changed && (row.down ? after < cur : after > cur),
    });
  }
  return out;
}
